import { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';

const TOOLTIP_STYLE = {
  background: '#0f1420',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 11
};

const katilimColors: Record<string, string> = {
  'Katılım Yok': '#6b7280',
  'Katılmak İstiyor': '#f59e0b',
  'Davet Gönderildi': '#3b82f6',
  'Katılacak': '#8b5cf6',
  'Katıldı': '#10b981'
};

interface Lead {
  id: string;
  ad_soyad: string;
  telefon: string;
  kaynak: string;
  durum: string;
  lansman_katilim_durumu: string;
  davetGonderildi: boolean;
  manuel_mi: boolean;
  olusturma_tarihi: string;
}

function pct(n: number, d: number): string {
  if (!d) return '0%';
  return Math.round((n / d) * 100) + '%';
}

interface ReportsProps {
  leads: Lead[];
}

export default function Reports({ leads }: ReportsProps) {
  const dailyTrend = useMemo(() => {
    const counts: Record<string, number> = {};

    leads.forEach((l) => {
      if (!l.olusturma_tarihi) return;
      const day = l.olusturma_tarihi.slice(0, 10);
      counts[day] = (counts[day] || 0) + 1;
    });

    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b, 'tr'))
      .map(([tarih, basvuru]) => ({
        tarih: tarih.slice(5),
        basvuru
      }));
  }, [leads]);

  const channelPerf = useMemo(() => {
    const data: Record<string, { toplam: number; satin: number; katildi: number }> = {};

    leads.forEach((l) => {
      if (!data[l.kaynak]) {
        data[l.kaynak] = { toplam: 0, satin: 0, katildi: 0 };
      }

      data[l.kaynak].toplam++;

      if (l.durum === 'Satın Aldı') {
        data[l.kaynak].satin++;
      }

      if (l.lansman_katilim_durumu === 'Katıldı') {
        data[l.kaynak].katildi++;
      }
    });

    return Object.entries(data).map(([kaynak, v]) => ({
      kaynak,
      ...v
    }));
  }, [leads]);

  const katilimDist = useMemo(() => {
    const c: Record<string, number> = {};

    leads.forEach((l) => {
      const durum = l.lansman_katilim_durumu || 'Katılım Yok';
      c[durum] = (c[durum] || 0) + 1;
    });

    return Object.entries(c).map(([name, value]) => ({ name, value }));
  }, [leads]);

  const total = leads.length;
  const invited = leads.filter((l) => l.davetGonderildi).length;
  const attended = leads.filter((l) => l.lansman_katilim_durumu === 'Katıldı').length;
  const bought = leads.filter((l) => l.durum === 'Satın Aldı').length;
  const manualCount = leads.filter((l) => l.manuel_mi).length;

  const conversionStats = [
    { label: 'Davet Dönüşümü', val: pct(invited, total) },
    { label: 'Katılım Oranı', val: pct(attended, invited) },
    { label: 'Satış Dönüşümü', val: pct(bought, attended) },
    { label: 'Manuel Oran', val: pct(manualCount, total) },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-white">Raporlar</h1>
        <p className="text-white/30 text-sm mt-0.5">Kanal performansı ve dönüşüm analizleri</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {conversionStats.map((s) => (
          <div key={s.label} className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5 text-center">
            <p className="text-3xl font-bold text-amber-400">{s.val}</p>
            <p className="text-white/40 text-xs mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5">
        <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-4">Günlük Başvuru Trendi</p>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={dailyTrend}>
            <XAxis
              dataKey="tarih"
              tick={{ fill: '#ffffff40', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Line
              type="monotone"
              dataKey="basvuru"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ fill: '#f59e0b', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5">
          <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-4">Kanal Performansı</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={channelPerf} barSize={14}>
              <XAxis
                dataKey="kaynak"
                tick={{ fill: '#ffffff40', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#ffffff60' }} />
              <Bar dataKey="toplam" fill="#f59e0b88" radius={[4, 4, 0, 0]} name="Toplam" />
              <Bar dataKey="satin" fill="#34d39988" radius={[4, 4, 0, 0]} name="Satin Aldi" />
              <Bar dataKey="katildi" fill="#60a5fa88" radius={[4, 4, 0, 0]} name="Katildi" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5">
          <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-4">Lansman Katılım Analizi</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={katilimDist}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
              >
                {katilimDist.map((e, i) => (
                  <Cell key={i} fill={katilimColors[e.name] || '#6b7280'} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>

          <div className="flex flex-wrap gap-2 mt-1">
            {katilimDist.map((d) => (
              <div key={d.name} className="flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: katilimColors[d.name] || '#6b7280' }}
                />
                <span className="text-white/40 text-[10px]">
                  {d.name} <strong className="text-white/60">{d.value}</strong>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5">
        <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-4">
          Davet - Katilim Donusumu (Kaynak Bazli)
        </p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={channelPerf} barSize={18}>
            <XAxis
              dataKey="kaynak"
              tick={{ fill: '#ffffff40', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: 11, color: '#ffffff60' }} />
            <Bar dataKey="toplam" fill="#a78bfa50" radius={[4, 4, 0, 0]} name="Basvuru" />
            <Bar dataKey="katildi" fill="#10b98188" radius={[4, 4, 0, 0]} name="Katildi" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}